// SF3 — Peak Season Predictor.
//
// A COUNTDOWN, NOT A RANKING. Rows are ordered by date and never by size, and
// the deadline is the loudest thing on each one: knowing November is big is
// useless on the first of November. A deadline already behind the seller is
// shown in red, because that is the single most actionable state here.
//
// An empty list is a real answer. A flat year produces no peaks, and showing
// its three largest months anyway would invent urgency the data does not carry.
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { FiAlertTriangle, FiInfo } from 'react-icons/fi';

import AuthContext from '../../../../context/AuthContext';
import local from '../Seasonal.module.css';
import { getPeaks } from '../services/seasonalApi';
import SyncProgress from '../../components/SyncProgress';

const inr = new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 });

const formatDate = (iso) => {
  if (!iso) return '—';
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso
    : date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const shortDate = (iso) => {
  if (!iso) return '—';
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso
    : date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
};

const UpcomingPeaks = ({ asin }) => {
  const authCtx = useContext(AuthContext);
  const token = authCtx?.token;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      setData(await getPeaks(token, { asin }));
    } catch (err) {
      setError(err);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [token, asin]);

  useEffect(() => { load(); }, [load]);

  const peaks = data?.peaks || [];

  return (
    <section className={local.card}>
      <div className={local.cardHead}>
        <h2 className={local.cardTitle}>What is coming</h2>
        {data?.available && (
          <span className={local.tierChip}>
            next {data.horizon_months} months · estimates
          </span>
        )}
      </div>
      <p className={local.muted}>
        Peaks in date order, with the date you need to have ordered by. Every
        figure is an estimate built from your own trend and seasonal pattern.
      </p>

      {error && (
        <div className={`${local.banner} ${error.reportUnavailable ? local.bannerWarn : local.bannerError}`}>
          <FiAlertTriangle className={local.bannerIcon} />
          <div>
            <strong>
              {error.notConnected ? 'No Amazon account connected'
                : error.progress ? 'Sync in progress'
                : error.reportUnavailable ? 'Data not ready' : 'Could not load the forecast'}
            </strong>
            <div>{error.message}</div>
            <SyncProgress progress={error.progress} />
          </div>
        </div>
      )}

      {loading && !data && <div className={local.emptyCell}>Loading…</div>}

      {/* The refusal, worded so it reads as not yet rather than as broken. */}
      {data && !data.available && (
        <div className={`${local.banner} ${local.bannerWarn}`}>
          <FiInfo className={local.bannerIcon} />
          <div>
            <strong>Not enough history to forecast</strong>
            <div>{data.reason}</div>
          </div>
        </div>
      )}

      {data?.available && (
        <>
          {peaks.length === 0 ? (
            <div className={local.emptyCell}>
              No month ahead clears {Math.round((data.peak_threshold || 1.25) * 100)}%
              of a normal month. A flat stretch is a real answer, not a gap.
            </div>
          ) : peaks.map((peak) => (
            <div key={peak.label} className={local.peakRow}>
              <div>
                <div className={local.peakWhen}>{shortDate(peak.peak_date)}</div>
                <span className={`${local.pill} ${peak.type === 'event' ? local.pillEvent : local.pillPattern}`}>
                  {peak.type === 'event' ? 'event' : 'pattern'}
                </span>
              </div>
              <div>
                <div className={local.peakName}>
                  {peak.name} {peak.year}
                  {peak.confidence === 'low' && (
                    <span className={`${local.pill} ${local.pillLow}`}>low confidence</span>
                  )}
                </div>
                <div className={local.peakDriver}>
                  {peak.driver}
                  {peak.events_unsized?.length > 0 && (
                    <> {peak.events_unsized.map((e) => e.name).join(', ')} is expected
                    but not sized, so it adds nothing to this figure.</>
                  )}
                </div>
              </div>
              <div className={local.peakSize}>
                {inr.format(Math.round(peak.expected_units))} units
                <div className={local.peakDriver}>
                  {peak.vs_normal_month.toFixed(1)}× a normal month
                </div>
              </div>
              <div className={peak.deadline_passed
                ? `${local.peakDeadline} ${local.peakLate}` : local.peakDeadline}>
                {peak.deadline_passed ? 'Order by ' : 'Order by '}
                {formatDate(peak.action_deadline)}
                {peak.deadline_passed && <div>already passed</div>}
              </div>
            </div>
          ))}

          {/* The cross-check and the competitive caveat, both of which change
              how much the numbers above are worth leaning on. */}
          {(data.year_on_year?.reason || data.competitive?.reason) && (
            <ul className={local.noteList}>
              {data.year_on_year?.reason && (
                <li><FiInfo className={local.noteIcon} />{data.year_on_year.reason}</li>
              )}
              {data.competitive?.reason && (
                <li><FiInfo className={local.noteIcon} />{data.competitive.reason}</li>
              )}
            </ul>
          )}
        </>
      )}

      {data?.notes?.length > 0 && (
        <ul className={local.noteList}>
          {data.notes.map((note) => (
            <li key={note}><FiInfo className={local.noteIcon} />{note}</li>
          ))}
        </ul>
      )}
    </section>
  );
};

export default UpcomingPeaks;
